import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BRAND_SANS_FONT } from '../design/theme';
import {
  // Real homepage capability artefacts, reused verbatim. These are flex/grid
  // compositions that fill any width.
  CitationTrace,
  CitationQuote,
  CitationChip,
  CitationConnector,
  CitationSource,
  CitationSourceIcon,
  CitationSourceBody,
  CitationSourceTitle,
  CitationSourceMeta,
  CitationSourceHighlight,
  GovCompare,
  GovPanel,
  GovPanelLabel,
  GovHeadline,
  GovSwatchRow,
  GovSwatch,
  GovArrow,
  GovChecklist,
  GovCheck,
} from '../../screens/home/HomePage.styles';
import AutorenewRounded from '@mui/icons-material/AutorenewRounded';
import ArrowForwardIosRounded from '@mui/icons-material/ArrowForwardIosRounded';
import QueryStatsRounded from '@mui/icons-material/QueryStatsRounded';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import AssessmentRounded from '@mui/icons-material/AssessmentRounded';
import TableChartRounded from '@mui/icons-material/TableChartRounded';
import TranslateRounded from '@mui/icons-material/TranslateRounded';
import FormatListBulletedRounded from '@mui/icons-material/FormatListBulletedRounded';

// =============================================================================
// Shared "deck production" proof visuals.
//
// Built as flex/grid CSS compositions (not fixed-viewBox SVG) so each one fills
// its container at any width, exactly like the homepage hero artefacts. Two of
// them (citations, governance) reuse the real homepage capability artefacts
// verbatim. Used by both the /use-cases bento and the /vs comparison pages so
// the two surfaces share one visual language.
//
// To swap any tile for a real screenshot or clip later, render an <img>/<video>
// in place of the visual component.
// =============================================================================

export const ACCENT = '#B83B1D';
export const ACCENT2 = '#D4694E';
export const INK = '#2E140A';
const WHITE = '#FFFFFF';

const tint = (a) => alpha(ACCENT, a);
const inkTint = (a) => alpha(INK, a);

const reduceMotion = { '@media (prefers-reduced-motion: reduce)': { animation: 'none' } };

// Graphics are muted a touch (saturation pulled back) so the colour recedes and
// the tile headline keeps the focus. Applied uniformly to every visual in
// DeckVisualPanel, including the reused homepage artefacts, so the family stays
// consistent. Same hues, just less saturated.
const GRAPHIC_FILTER = 'saturate(0.8)';

// --- shared primitives -------------------------------------------------------

// A row of bars that flexes to fill width; heights are percentages of the row.
// Matches the landing hero "Revenue Projection" chart exactly: flat tint bars
// with a single gradient highlight bar (soft accent shadow). Optional month
// labels echo that card's .float-card__chart-month row.
const Bars = ({ data, labels, sx }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.6, ...sx }}>
    {data.map((d, i) => (
      <Box
        key={i}
        sx={{
          flex: 1,
          minWidth: 0,
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: labels ? 0.4 : 0,
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: `${d.h}%`,
            borderRadius: '3px 3px 0 0',
            background: d.accent
              ? `linear-gradient(180deg, ${ACCENT} 0%, ${ACCENT2} 100%)`
              : tint(0.15),
            boxShadow: d.accent ? `0 2px 8px ${tint(0.2)}` : 'none',
            transformOrigin: 'bottom',
            ...(d.grow
              ? {
                  '@keyframes ucBarGrow': {
                    '0%': { transform: 'scaleY(0.72)' },
                    '100%': { transform: 'scaleY(1)' },
                  },
                  animation: 'ucBarGrow 2.6s ease-in-out infinite alternate',
                  ...reduceMotion,
                }
              : null),
          }}
        />
        {labels && (
          <Typography
            sx={{
              fontFamily: BRAND_SANS_FONT,
              fontSize: '0.5rem',
              fontWeight: d.accent ? 600 : 450,
              lineHeight: 1,
              textTransform: 'uppercase',
              color: d.accent ? ACCENT : inkTint(0.35),
            }}
          >
            {labels[i]}
          </Typography>
        )}
      </Box>
    ))}
  </Box>
);

// Placeholder text lines (varying widths) to suggest body copy on a slide.
const Lines = ({ n = 3, widths = ['100%', '84%', '58%'], color, sx }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ...sx }}>
    {Array.from({ length: n }).map((_, i) => (
      <Box
        key={i}
        sx={{
          height: 3,
          borderRadius: 2,
          width: widths[i % widths.length],
          background: color || inkTint(0.12),
        }}
      />
    ))}
  </Box>
);

// A single slide thumbnail card. Soft, low-texture: light border, faint shadow.
const SlideThumb = ({ active, children, sx }) => (
  <Box
    sx={{
      position: 'relative',
      borderRadius: '9px',
      background: WHITE,
      border: `1px solid ${active ? tint(0.3) : inkTint(0.08)}`,
      boxShadow: active
        ? `0 2px 8px ${tint(0.08)}`
        : `0 1px 4px ${inkTint(0.04)}`,
      p: 1.1,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.7,
      overflow: 'hidden',
      ...sx,
    }}
  >
    {children}
  </Box>
);

// Small badge pill (Q4 / LP / Wk etc.).
const CycleBadge = ({ label }) => (
  <Box
    sx={{
      position: 'absolute',
      top: 8,
      left: 8,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.4,
      px: 0.9,
      py: 0.3,
      borderRadius: '999px',
      background: ACCENT,
      color: WHITE,
      fontFamily: BRAND_SANS_FONT,
      fontSize: '0.62rem',
      fontWeight: 700,
      letterSpacing: '0.02em',
      boxShadow: `0 3px 8px ${tint(0.3)}`,
      zIndex: 3,
    }}
  >
    <AutorenewRounded sx={{ fontSize: '0.72rem' }} />
    {label}
  </Box>
);

// A thin chevron used between before/after stages.
const Flow = ({ sx }) => (
  <Box
    sx={{
      flex: '0 0 auto',
      color: tint(0.55),
      display: 'flex',
      alignItems: 'center',
      ...sx,
    }}
  >
    <ArrowForwardIosRounded sx={{ fontSize: '0.8rem' }} />
  </Box>
);

// Small section label used inside split visuals.
const VLabel = ({ children, accent }) => (
  <Typography
    sx={{
      fontFamily: BRAND_SANS_FONT,
      fontSize: '0.56rem',
      fontWeight: 700,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: accent ? ACCENT : inkTint(0.45),
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Typography>
);

// --- tiny SVGs (only where CSS can't draw the curve: line + donut) ----------

const LineMini = () => (
  <Box
    component="svg"
    viewBox="0 0 100 56"
    preserveAspectRatio="none"
    sx={{ width: '100%', flex: 1, display: 'block', overflow: 'visible' }}
  >
    <defs>
      <linearGradient id="uc-line-fill" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={ACCENT} stopOpacity="0.18" />
        <stop offset="100%" stopColor={ACCENT} stopOpacity="0" />
      </linearGradient>
    </defs>
    <path d="M2 46 L22 36 L40 40 L60 22 L80 28 L98 10 L98 56 L2 56 Z" fill="url(#uc-line-fill)" />
    <polyline
      points="2,46 22,36 40,40 60,22 80,28 98,10"
      fill="none"
      stroke={ACCENT}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
    />
    <circle cx="60" cy="22" r="2.4" fill={ACCENT} />
    <circle cx="98" cy="10" r="2.4" fill={ACCENT2} />
  </Box>
);

const DonutMini = () => (
  <Box
    component="svg"
    viewBox="0 0 60 60"
    sx={{ width: '100%', flex: 1, display: 'block' }}
  >
    <circle cx="30" cy="30" r="18" fill="none" stroke={tint(0.16)} strokeWidth="9" />
    <circle
      cx="30"
      cy="30"
      r="18"
      fill="none"
      stroke={ACCENT}
      strokeWidth="9"
      strokeDasharray="70 113"
      strokeLinecap="round"
      transform="rotate(-90 30 30)"
    />
    <circle
      cx="30"
      cy="30"
      r="18"
      fill="none"
      stroke={ACCENT2}
      strokeWidth="9"
      strokeDasharray="28 113"
      strokeDashoffset="-72"
      strokeLinecap="round"
      transform="rotate(-90 30 30)"
    />
  </Box>
);

// =============================================================================
// Visual compositions (one per use-case / comparison kind)
// =============================================================================

// Recurring data refresh: spreadsheet -> refresh -> updated KPI + bar chart.
const VisualDataRefresh = () => (
  <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.25, width: '100%', height: '100%' }}>
    {/* spreadsheet with two changed cells highlighted */}
    <Box
      sx={{
        flex: '0 0 34%',
        borderRadius: '9px',
        background: WHITE,
        border: `1px solid ${inkTint(0.08)}`,
        boxShadow: `0 1px 4px ${inkTint(0.04)}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ height: 16, background: tint(0.12), display: 'flex', alignItems: 'center', px: 0.8, gap: 0.5 }}>
        <TableChartRounded sx={{ fontSize: '0.7rem', color: ACCENT }} />
        <Box sx={{ height: 3, width: '46%', borderRadius: 2, background: tint(0.4) }} />
      </Box>
      <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridAutoRows: '1fr' }}>
        {Array.from({ length: 12 }).map((_, i) => (
          <Box
            key={i}
            sx={{
              borderRight: `1px solid ${inkTint(0.06)}`,
              borderBottom: `1px solid ${inkTint(0.06)}`,
              background: i === 4 || i === 7 ? tint(0.16) : 'transparent',
            }}
          />
        ))}
      </Box>
    </Box>

    <Flow sx={{ alignSelf: 'center' }} />

    {/* updated chart card */}
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        borderRadius: '9px',
        background: WHITE,
        border: `1px solid ${tint(0.22)}`,
        boxShadow: `0 1px 5px ${tint(0.07)}`,
        p: 1.2,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.6,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '1.4rem', fontWeight: 800, letterSpacing: '-0.03em', color: INK, lineHeight: 1 }}>
          $340M
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.3,
            px: 0.7,
            py: 0.2,
            borderRadius: '999px',
            background: tint(0.12),
            color: ACCENT,
            fontFamily: BRAND_SANS_FONT,
            fontSize: '0.62rem',
            fontWeight: 700,
          }}
        >
          <TrendingUpRounded sx={{ fontSize: '0.72rem' }} /> +41%
        </Box>
      </Box>
      <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.6rem', fontWeight: 450, color: inkTint(0.45), lineHeight: 1 }}>
        Projected revenue
      </Typography>
      <Bars
        data={[{ h: 38 }, { h: 52 }, { h: 47 }, { h: 66 }, { h: 92, accent: true, grow: true }, { h: 74 }]}
        labels={['May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct']}
        sx={{ flex: 1, minHeight: 0, mt: 0.2 }}
      />
    </Box>
  </Box>
);

// A stacked deck / pack: one active slide with a chart, two backers, cycle badge.
const VisualDeck = ({ badge, chart = 'bars' }) => (
  <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
    {/* backer slides */}
    <Box
      sx={{
        position: 'absolute',
        top: 26,
        left: 34,
        right: 6,
        bottom: 6,
        borderRadius: '9px',
        background: WHITE,
        border: `1px solid ${inkTint(0.08)}`,
        opacity: 0.55,
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        top: 16,
        left: 20,
        right: 18,
        bottom: 16,
        borderRadius: '9px',
        background: WHITE,
        border: `1px solid ${inkTint(0.09)}`,
        opacity: 0.8,
      }}
    />
    {/* active slide */}
    <SlideThumb
      active
      sx={{ position: 'absolute', top: 4, left: 4, right: 32, bottom: 26 }}
    >
      <Box sx={{ height: 5, width: '52%', borderRadius: 2, background: ACCENT }} />
      {chart === 'line' ? (
        <LineMini />
      ) : (
        <Bars data={[{ h: 44 }, { h: 66, accent: true }, { h: 52 }, { h: 84, accent: true }, { h: 62 }]} sx={{ flex: 1, mt: 0.4 }} />
      )}
    </SlideThumb>
    {badge && <CycleBadge label={badge} />}
  </Box>
);

// Value-creation / 100-day plan: phase bars with a "now" marker.
const PLAN_BARS = [
  { top: '8%', left: '4%', width: '60%', label: 'Diligence' },
  { top: '32%', left: '18%', width: '58%', label: 'Onboarding' },
  { top: '56%', left: '30%', width: '62%', label: 'Quick wins' },
  { top: '80%', left: '46%', width: '50%', label: 'Scale' },
];
const VisualPlan = () => (
  <Box sx={{ position: 'relative', width: '100%', height: '100%', px: 0.5 }}>
    {PLAN_BARS.map((b, i) => (
      <Box
        key={i}
        sx={{
          position: 'absolute',
          top: b.top,
          left: b.left,
          width: b.width,
          height: 12,
          borderRadius: '6px',
          background: i % 2 ? tint(0.24) : `linear-gradient(90deg, ${ACCENT} 0%, ${ACCENT2} 100%)`,
          display: 'flex',
          alignItems: 'center',
          pl: 0.8,
        }}
      >
        <Typography
          sx={{
            fontFamily: BRAND_SANS_FONT,
            fontSize: '0.56rem',
            fontWeight: 600,
            color: i % 2 ? ACCENT : WHITE,
            whiteSpace: 'nowrap',
          }}
        >
          {b.label}
        </Typography>
      </Box>
    ))}
    {/* "now" marker */}
    <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: '42%', width: 0 }}>
      <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: 0, borderLeft: `1.5px dashed ${tint(0.55)}` }} />
      <Box sx={{ position: 'absolute', top: -2, left: -3, width: 7, height: 7, borderRadius: '50%', background: ACCENT }} />
      <Typography
        sx={{
          position: 'absolute',
          top: -4,
          left: 8,
          fontFamily: BRAND_SANS_FONT,
          fontSize: '0.55rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: ACCENT,
        }}
      >
        Now
      </Typography>
    </Box>
  </Box>
);

// Connected to your data stack: data sources feed a central Restack hub.
// Borrows the landing connector-capability language (central hub + radial
// sources + dashed links), compact and symmetric so it stays clean and correct.
const SrcChip = ({ name, icon }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      px: 0.9,
      py: 0.45,
      borderRadius: '999px',
      background: WHITE,
      border: `1px solid ${inkTint(0.08)}`,
      boxShadow: `0 1px 3px ${inkTint(0.04)}`,
      fontFamily: BRAND_SANS_FONT,
      fontSize: '0.6rem',
      fontWeight: 600,
      color: INK,
      whiteSpace: 'nowrap',
      '& svg': { color: ACCENT, fontSize: '0.78rem' },
    }}
  >
    {icon}
    {name}
  </Box>
);
const SRC_LEFT = [
  { name: 'Capital IQ', icon: <AssessmentRounded /> },
  { name: 'Bloomberg', icon: <QueryStatsRounded /> },
];
const SRC_RIGHT = [
  { name: 'PitchBook', icon: <TrendingUpRounded /> },
  { name: 'Your files', icon: <TableChartRounded /> },
];
const VisualConnectors = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '100%', height: '100%' }}>
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.7 }}>
      {SRC_LEFT.map((s) => (
        <SrcChip key={s.name} {...s} />
      ))}
    </Box>
    <Box sx={{ flex: '0 0 20px', borderTop: `1.5px dashed ${tint(0.35)}` }} />
    {/* hub */}
    <Box
      sx={{
        position: 'relative',
        flexShrink: 0,
        width: 46,
        height: 46,
        borderRadius: '50%',
        background: `linear-gradient(135deg, #1e1612 0%, #3a2a22 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: WHITE,
        fontFamily: BRAND_SANS_FONT,
        fontWeight: 700,
        fontSize: '0.95rem',
        boxShadow: `0 8px 24px ${inkTint(0.16)}, inset 0 1px 0 ${alpha('#ffffff', 0.08)}`,
        // Static dashed orbit ring, echoing the landing ConnectorOrbit.
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: -7,
          borderRadius: '50%',
          border: `1px dashed ${inkTint(0.12)}`,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: -4,
          borderRadius: '50%',
          border: `1px solid ${tint(0.35)}`,
          '@keyframes ucHubPulse': { '0%': { transform: 'scale(1)', opacity: 0.6 }, '100%': { transform: 'scale(1.4)', opacity: 0 } },
          animation: 'ucHubPulse 2.8s ease-out infinite',
          ...reduceMotion,
        },
      }}
    >
      R
    </Box>
    <Box sx={{ flex: '0 0 20px', borderTop: `1.5px dashed ${tint(0.35)}` }} />
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.7 }}>
      {SRC_RIGHT.map((s) => (
        <SrcChip key={s.name} {...s} />
      ))}
    </Box>
  </Box>
);

// Exit materials / management presentations: a clean cover slide plus a
// structured contents list.
const CIM_SECTIONS = ['Market', 'Financials', 'Plan'];
const VisualSlideBuild = () => (
  <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.4, width: '100%', height: '100%' }}>
    {/* cover slide */}
    <SlideThumb active sx={{ flex: 1.05, justifyContent: 'center', gap: 0.9, p: 1.4 }}>
      <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: ACCENT }} />
      <Box sx={{ height: 9, width: '82%', borderRadius: 2, background: INK }} />
      <Box sx={{ height: 9, width: '56%', borderRadius: 2, background: tint(0.7) }} />
      <Box sx={{ height: 3, width: '32%', borderRadius: 2, background: ACCENT, mt: 0.2 }} />
      <Box sx={{ flex: 1 }} />
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          alignSelf: 'flex-start',
          px: 0.9,
          py: 0.4,
          borderRadius: '999px',
          border: `1px solid ${tint(0.3)}`,
          background: tint(0.08),
        }}
      >
        <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: ACCENT }} />
        <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.06em', color: ACCENT }}>
          DECK
        </Typography>
      </Box>
    </SlideThumb>

    {/* structured contents */}
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.7, justifyContent: 'center', minWidth: 0 }}>
      {CIM_SECTIONS.map((s, i) => (
        <Box
          key={s}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.9,
            p: 1,
            borderRadius: '9px',
            background: WHITE,
            border: `1px solid ${i === 0 ? tint(0.3) : inkTint(0.1)}`,
            boxShadow: i === 0 ? `0 2px 7px ${tint(0.1)}` : `0 1px 3px ${inkTint(0.03)}`,
          }}
        >
          <Box
            sx={{
              width: 22,
              height: 22,
              flexShrink: 0,
              borderRadius: '6px',
              background: i === 0 ? ACCENT : tint(0.12),
              color: i === 0 ? WHITE : ACCENT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: BRAND_SANS_FONT,
              fontWeight: 700,
              fontSize: '0.62rem',
            }}
          >
            {`0${i + 1}`}
          </Box>
          <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.76rem', fontWeight: 600, color: INK, whiteSpace: 'nowrap' }}>
            {s}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <ArrowForwardIosRounded sx={{ fontSize: '0.6rem', color: tint(0.5) }} />
        </Box>
      ))}
    </Box>
  </Box>
);

// Research / chat to client-ready slides: a cited claim, traced to a source,
// flowing into a polished slide. Reuses the real homepage CitationChip and
// mirrors its claim -> source-highlight flow, so this reads as a sibling of the
// landing IC-memo citation trace rather than a row of loose chips.
const VisualResearch = () => (
  <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.4, width: '100%', height: '100%' }}>
    {/* research message: a cited claim, traced to a source */}
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        borderRadius: '12px',
        background: WHITE,
        border: `1px solid ${inkTint(0.08)}`,
        boxShadow: `0 1px 4px ${inkTint(0.04)}`,
        p: 1.2,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.7,
        justifyContent: 'center',
      }}
    >
      {/* who is speaking */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
        <Box sx={{ width: 14, height: 14, borderRadius: '50%', background: tint(0.16), border: `1px solid ${tint(0.3)}`, flexShrink: 0 }} />
        <Box sx={{ height: 3, width: '40%', borderRadius: 2, background: inkTint(0.18) }} />
      </Box>
      {/* the cited claim, with the real homepage chips inline at line end */}
      <Box sx={{ pl: 0.9, borderLeft: `2px solid ${tint(0.22)}`, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ flex: 1, height: 3, borderRadius: 2, background: inkTint(0.14) }} />
          <CitationChip>1</CitationChip>
          <CitationChip>2</CitationChip>
        </Box>
        <Box sx={{ height: 3, width: '72%', borderRadius: 2, background: inkTint(0.12) }} />
      </Box>
      {/* traced to a source: PDF tag + highlighted passage */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
        <Box
          sx={{
            flexShrink: 0,
            px: 0.5,
            py: '1px',
            borderRadius: '4px',
            background: tint(0.08),
            border: `1px solid ${tint(0.22)}`,
            color: ACCENT,
            fontFamily: BRAND_SANS_FONT,
            fontSize: '0.5rem',
            fontWeight: 700,
            letterSpacing: '0.04em',
            lineHeight: 1.4,
          }}
        >
          PDF
        </Box>
        <Box
          sx={{
            flex: 1,
            height: 5,
            borderRadius: 3,
            background: `linear-gradient(90deg, ${tint(0.32)} 0%, ${tint(0.12)} 100%)`,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: '52%',
              borderRadius: 3,
              background: tint(0.5),
            },
          }}
        />
      </Box>
    </Box>

    <Flow sx={{ alignSelf: 'center' }} />

    {/* finished slide */}
    <SlideThumb active sx={{ flex: 1.05, gap: 0.6 }}>
      <Box sx={{ height: 5, width: '58%', borderRadius: 2, background: ACCENT }} />
      <Lines n={2} widths={['100%', '82%']} sx={{ mt: 0.2 }} />
      <Bars data={[{ h: 48 }, { h: 70, accent: true }, { h: 56 }, { h: 86, accent: true }]} sx={{ flex: 1, mt: 0.5 }} />
    </SlideThumb>
  </Box>
);

// Template & brand migration: a calm legacy frame -> a clean on-brand frame.
const VisualTemplateSwap = () => (
  <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.1, width: '100%', height: '100%' }}>
    {/* legacy: a real but off-brand slide, deliberately flat and grey, with a
        dashed frame to read as the "before" (mirrors the governance before). */}
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        borderRadius: '9px',
        background: WHITE,
        border: `1px dashed ${inkTint(0.18)}`,
        p: 1.2,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.7,
        justifyContent: 'center',
      }}
    >
      <Box sx={{ height: 5, width: '52%', borderRadius: 2, background: inkTint(0.28) }} />
      <Lines n={2} widths={['100%', '78%']} color={inkTint(0.1)} sx={{ mt: 0.2 }} />
      {/* dull, off-brand chart */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.6, height: 28, mt: 0.3 }}>
        {[46, 64, 54, 78].map((h, i) => (
          <Box key={i} sx={{ flex: 1, height: `${h}%`, borderRadius: '3px 3px 0 0', background: inkTint(0.12) }} />
        ))}
      </Box>
    </Box>

    <Flow sx={{ alignSelf: 'center' }} />

    {/* on-brand: crisp accent header + body + one small chart */}
    <SlideThumb active sx={{ flex: 1, gap: 0.7, justifyContent: 'center' }}>
      <Box sx={{ height: 5, width: '52%', borderRadius: 2, background: ACCENT }} />
      <Lines n={2} widths={['100%', '78%']} color={tint(0.18)} sx={{ mt: 0.2 }} />
      <Bars data={[{ h: 46 }, { h: 72, accent: true }, { h: 56 }, { h: 84, accent: true }]} sx={{ height: 28, mt: 0.3 }} />
    </SlideThumb>
  </Box>
);

// Chart-heavy rebuilds: a wall of enlarged charts.
const ChartCard = ({ children, sx }) => (
  <Box
    sx={{
      borderRadius: '9px',
      background: WHITE,
      border: `1px solid ${inkTint(0.08)}`,
      boxShadow: `0 1px 4px ${inkTint(0.04)}`,
      p: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      minWidth: 0,
      ...sx,
    }}
  >
    {children}
  </Box>
);
const VisualCharts = () => (
  <Box sx={{ display: 'flex', gap: 1, width: '100%', height: '100%' }}>
    <ChartCard sx={{ flex: 1 }}>
      <Box sx={{ height: 4, width: '52%', borderRadius: 2, background: tint(0.55) }} />
      <Bars data={[{ h: 46 }, { h: 70, accent: true }, { h: 56 }, { h: 88, accent: true }]} sx={{ flex: 1, mt: 0.4 }} />
    </ChartCard>
    <ChartCard sx={{ flex: 1 }}>
      <Box sx={{ height: 4, width: '44%', borderRadius: 2, background: tint(0.55) }} />
      <LineMini />
    </ChartCard>
    <ChartCard sx={{ flex: 1 }}>
      <Box sx={{ height: 4, width: '48%', borderRadius: 2, background: tint(0.55) }} />
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ width: 46, height: 46 }}>
          <DonutMini />
        </Box>
      </Box>
    </ChartCard>
  </Box>
);

// Proposals & pitch decks: a cover plus three consistent content slides.
const VisualProposal = () => (
  <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.4, width: '100%', height: '100%' }}>
    {/* cover */}
    <SlideThumb active sx={{ flex: 1.05, justifyContent: 'center', gap: 0.9, p: 1.4 }}>
      <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: ACCENT }} />
      <Box sx={{ height: 9, width: '78%', borderRadius: 2, background: INK }} />
      <Box sx={{ height: 9, width: '52%', borderRadius: 2, background: tint(0.7) }} />
      <Box sx={{ height: 3, width: '32%', borderRadius: 2, background: ACCENT, mt: 0.3 }} />
    </SlideThumb>
    {/* content slides: same header + body rhythm, one chart element each */}
    <SlideThumb sx={{ flex: 1, gap: 0.6 }}>
      <Box sx={{ height: 4, width: '58%', borderRadius: 2, background: tint(0.55) }} />
      <Lines n={2} widths={['100%', '82%']} sx={{ mt: 0.2 }} />
      <Bars data={[{ h: 50 }, { h: 74, accent: true }, { h: 60 }]} sx={{ flex: 1, mt: 0.5 }} />
    </SlideThumb>
    <SlideThumb sx={{ flex: 1, gap: 0.6 }}>
      <Box sx={{ height: 4, width: '52%', borderRadius: 2, background: tint(0.55) }} />
      <Lines n={2} widths={['100%', '78%']} sx={{ mt: 0.2 }} />
      <Box sx={{ flex: 1, mt: 0.5, display: 'flex', alignItems: 'flex-end' }}>
        <LineMini />
      </Box>
    </SlideThumb>
    <SlideThumb sx={{ flex: 1, gap: 0.6 }}>
      <Box sx={{ height: 4, width: '60%', borderRadius: 2, background: tint(0.55) }} />
      <Lines n={5} widths={['100%', '94%', '84%', '72%', '50%']} sx={{ mt: 0.2 }} />
    </SlideThumb>
  </Box>
);

// Horizontal (general, any document) vs vertical (deal-team, deep). Used on the
// /vs/claude page to contrast a broad editor with the deal-team build.
const DEAL_ROWS = ['IC memo', 'Board pre-read', 'SteerCo'];
const VisualVertical = () => (
  <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.25, width: '100%', height: '100%' }}>
    {/* horizontal: any document */}
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.6, minWidth: 0 }}>
      <VLabel>General: any document</VLabel>
      <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.6 }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Box
            key={i}
            sx={{
              borderRadius: '7px',
              background: WHITE,
              border: `1px solid ${inkTint(0.1)}`,
              p: 0.9,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.4,
              justifyContent: 'center',
            }}
          >
            <Box sx={{ height: 3, width: '50%', borderRadius: 2, background: inkTint(0.2) }} />
            <Lines n={2} widths={['100%', '70%']} color={inkTint(0.1)} />
          </Box>
        ))}
      </Box>
    </Box>

    <Flow sx={{ alignSelf: 'center' }} />

    {/* vertical: deal team */}
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.6, minWidth: 0 }}>
      <VLabel accent>Deal team: deep</VLabel>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.55 }}>
        {DEAL_ROWS.map((name, i) => (
          <SlideThumb
            key={name}
            active={i === 0}
            sx={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 0.7, py: 0.6, px: 0.9 }}
          >
            <Box sx={{ width: 3, alignSelf: 'stretch', borderRadius: 2, background: ACCENT }} />
            <Typography
              sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.66rem', fontWeight: 600, color: INK, whiteSpace: 'nowrap' }}
            >
              {name}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Bars
              data={[{ h: 50 }, { h: 80, accent: true }, { h: 64 }]}
              sx={{ width: 26, height: 16, gap: 0.4 }}
            />
          </SlideThumb>
        ))}
      </Box>
    </Box>
  </Box>
);

// IC memo: the real homepage citations artefact (claim -> source trace).
const VisualCitations = () => (
  <CitationTrace>
    <CitationQuote>
      &nbsp;&nbsp;Nordic fintech TAM grew 23% YoY to &euro;4.2B in 2026
      <CitationChip>1</CitationChip>
      <CitationChip>2</CitationChip>
    </CitationQuote>
    <CitationConnector />
    <CitationSource>
      <CitationSourceIcon />
      <CitationSourceBody>
        <CitationSourceTitle>Industry Report Q3 2026</CitationSourceTitle>
        <CitationSourceMeta>Page 14 &middot; Paragraph 3</CitationSourceMeta>
        <CitationSourceHighlight />
      </CitationSourceBody>
    </CitationSource>
  </CitationTrace>
);

// Brand compliance: the real homepage governance artefact (before/after).
const VisualGovernance = () => (
  <Box sx={{ width: '100%' }}>
    <GovCompare>
      <GovPanel variant="bad">
        <GovPanelLabel variant="bad">Generic AI</GovPanelLabel>
        <GovHeadline variant="bad">Market Entry Strategy</GovHeadline>
        <GovSwatchRow>
          <GovSwatch color="#3B82F6" />
          <GovSwatch color="#93C5FD" />
          <GovSwatch color="#BFDBFE" />
        </GovSwatchRow>
      </GovPanel>
      <GovArrow className="gov-arrow">&rarr;</GovArrow>
      <GovPanel variant="good">
        <GovPanelLabel variant="good">On your template</GovPanelLabel>
        <GovHeadline variant="good">Market Entry Strategy</GovHeadline>
        <GovSwatchRow>
          <GovSwatch color="#B83B1D" />
          <GovSwatch color="#D4694E" />
          <GovSwatch color="#E8B8A4" />
        </GovSwatchRow>
      </GovPanel>
    </GovCompare>
    <GovChecklist>
      <GovCheck>Typography</GovCheck>
      <GovCheck>Palette</GovCheck>
      <GovCheck>Margins</GovCheck>
      <GovCheck>Disclaimers</GovCheck>
    </GovChecklist>
  </Box>
);

// Bilingual / multi-market decks: the same slide on the same template, in two
// languages. Two identical layouts flanking a translate marker.
const LangSlide = ({ lang }) => (
  <SlideThumb active sx={{ flex: 1, gap: 0.6, position: 'relative' }}>
    <Box
      sx={{
        position: 'absolute',
        top: 8,
        right: 8,
        px: 0.7,
        py: 0.15,
        borderRadius: '999px',
        background: tint(0.12),
        border: `1px solid ${tint(0.28)}`,
        color: ACCENT,
        fontFamily: BRAND_SANS_FONT,
        fontSize: '0.55rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        zIndex: 2,
      }}
    >
      {lang}
    </Box>
    <Box sx={{ height: 5, width: '54%', borderRadius: 2, background: ACCENT }} />
    <Lines n={2} widths={['100%', '80%']} sx={{ mt: 0.2 }} />
    <Bars data={[{ h: 48 }, { h: 72, accent: true }, { h: 58 }]} sx={{ flex: 1, mt: 0.4 }} />
  </SlideThumb>
);
const VisualBilingual = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, width: '100%', height: '100%' }}>
    <LangSlide lang="EN" />
    <Box
      sx={{
        flexShrink: 0,
        width: 26,
        height: 26,
        borderRadius: '50%',
        background: WHITE,
        border: `1px solid ${inkTint(0.1)}`,
        boxShadow: `0 1px 4px ${inkTint(0.05)}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: ACCENT,
        '& svg': { fontSize: '0.95rem' },
      }}
    >
      <TranslateRounded />
    </Box>
    <LangSlide lang="FR" />
  </Box>
);

// Workshop / training materials: your outline becomes a session deck.
const WORKSHOP_ITEMS = ['Warm-up', 'Exercise', 'Debrief'];
const VisualWorkshop = () => (
  <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.4, width: '100%', height: '100%' }}>
    {/* outline source */}
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        borderRadius: '9px',
        background: WHITE,
        border: `1px solid ${inkTint(0.08)}`,
        boxShadow: `0 1px 4px ${inkTint(0.04)}`,
        p: 1.2,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.6,
        justifyContent: 'center',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <FormatListBulletedRounded sx={{ fontSize: '0.8rem', color: ACCENT }} />
        <Box sx={{ height: 3, width: '40%', borderRadius: 2, background: inkTint(0.18) }} />
      </Box>
      {WORKSHOP_ITEMS.map((s) => (
        <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <Box sx={{ width: 4, height: 4, borderRadius: '50%', flexShrink: 0, background: tint(0.5) }} />
          <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.62rem', fontWeight: 600, color: INK, whiteSpace: 'nowrap' }}>
            {s}
          </Typography>
        </Box>
      ))}
    </Box>

    <Flow sx={{ alignSelf: 'center' }} />

    {/* session slide */}
    <SlideThumb active sx={{ flex: 1.05, gap: 0.6 }}>
      <Box sx={{ height: 5, width: '56%', borderRadius: 2, background: ACCENT }} />
      <Lines n={2} widths={['100%', '78%']} sx={{ mt: 0.2 }} />
      <Bars data={[{ h: 46 }, { h: 70, accent: true }, { h: 58 }, { h: 84, accent: true }]} sx={{ flex: 1, mt: 0.4 }} />
    </SlideThumb>
  </Box>
);

// kind: 'fill' -> CSS visual that fills a fixed-height panel.
// kind: 'art'  -> reused homepage artefact, sits at its natural height.
export const DECK_VISUALS = {
  dataRefresh: { kind: 'fill', render: () => <VisualDataRefresh /> },
  deckQ: { kind: 'fill', render: () => <VisualDeck badge="Q4" chart="bars" /> },
  deckLP: { kind: 'fill', render: () => <VisualDeck badge="LP" chart="line" /> },
  deckWk: { kind: 'fill', render: () => <VisualDeck badge="Wk" chart="bars" /> },
  deckPipe: { kind: 'fill', render: () => <VisualDeck badge="New" chart="bars" /> },
  plan: { kind: 'fill', render: () => <VisualPlan /> },
  connectors: { kind: 'fill', render: () => <VisualConnectors /> },
  slideBuild: { kind: 'fill', render: () => <VisualSlideBuild /> },
  research: { kind: 'fill', render: () => <VisualResearch /> },
  templateSwap: { kind: 'fill', render: () => <VisualTemplateSwap /> },
  charts: { kind: 'fill', render: () => <VisualCharts /> },
  proposal: { kind: 'fill', render: () => <VisualProposal /> },
  bilingual: { kind: 'fill', render: () => <VisualBilingual /> },
  workshop: { kind: 'fill', render: () => <VisualWorkshop /> },
  vertical: { kind: 'fill', render: () => <VisualVertical /> },
  citations: { kind: 'art', render: () => <VisualCitations /> },
  governance: { kind: 'art', render: () => <VisualGovernance /> },
};

// A framed visual panel.
//   variant 'top'        full-bleed banner with a border below (text sits under)
//   variant 'standalone' full rounded border (used on the comparison hero tile)
//   variant 'support'    small, light, contained strip that SUPPORTS the text
//                        above it rather than leading the tile (Apple-bento)
// Fixed height for 'fill' visuals, natural height for reused 'art' artefacts.
export const DeckVisualPanel = ({ visual, variant = 'top', sx }) => {
  const v = DECK_VISUALS[visual];
  if (!v) return null;
  const isArt = v.kind === 'art';
  const support = variant === 'support';
  const fillH = support ? { xs: 100, md: 126 } : { xs: 150, md: 184 };
  const variantSx =
    variant === 'standalone'
      ? { border: `1px solid ${inkTint(0.09)}`, borderRadius: '16px' }
      : support
      ? { border: `1px solid ${inkTint(0.06)}`, borderRadius: '12px', background: inkTint(0.02) }
      : { borderBottom: `1px solid ${inkTint(0.08)}` };
  return (
    <Box
      sx={{
        height: isArt ? 'auto' : fillH,
        minHeight: isArt ? fillH : undefined,
        px: support ? 1.5 : 2,
        py: support ? 1.1 : 1.75,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${tint(0.05)} 0%, ${inkTint(0.015)} 100%)`,
        filter: GRAPHIC_FILTER,
        ...variantSx,
        ...sx,
      }}
    >
      {v.render()}
    </Box>
  );
};
